import { Injectable } from '@angular/core';
import { catchError, Observable, tap, throwError } from 'rxjs';
import { LoginData, LoginResponse, Token, User } from '../../models/auth/login.model';
import { environment } from '../../../environments/environment';
import { map } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthStateService } from './auth-state.service';
import { HttpGenericService } from '../api/http-generic.service';
import { IndexedDbService } from '../core/utils/indexed-db.service';
import { ResponseWrapper } from '../../models/api/response-wrapper.model';
import { AdmSeguridadService } from '../core/seguridad/adm-seguridad.service';
import { MenuAngularBuilder } from '../core/seguridad/menu-angular.builder';

@Injectable({ providedIn: 'root' })
export class AuthService {
  constructor(
    private readonly http: HttpGenericService,
    private readonly router: Router,
    private readonly indexedDbService: IndexedDbService,
    private readonly authState: AuthStateService,
    private readonly admSeguridad: AdmSeguridadService,
    private readonly menuBuilder: MenuAngularBuilder,
  ) {}

  login(data: LoginData): Observable<ResponseWrapper<LoginResponse>> {
    return this.http.postFullPublic<LoginResponse, LoginData>('login/auth', data, environment.KONG_SERVICE_URL);
  }

  refreshToken() {
    const tok = this.authState.token;
    if (!this.authState.rtEsValido(tok)) {
      void this.cerrarSesion();
      return throwError(() => new Error('Refresh token expirado'));
    }

    const rt = this.getCookie('SW_RT');
    if (!rt) {
      return throwError(() => new Error('No hay refresh token en la cookie'));
    }

    const body = { refreshToken: rt };

    return this.http
      .postPublic<
        { tokenAcceso: string; expiracion: string },
        typeof body
      >('token/refresh', body, environment.KONG_SERVICE_URL)
      .pipe(
        tap(async ({ tokenAcceso, expiracion }) => {
          await this.authState.aplicarNuevoToken({ tokenAcceso, expiracion });
        }),
        catchError((err) => {
          console.error('Error al refrescar token', err);
          return throwError(() => new Error('No se pudo refrescar el token'));
        }),
      );
  }

  /**
   * Construye el menú del usuario y devuelve las rutas que tiene permitidas.
   *
   * sw-core entrega la lista plana de opciones (`sw_spadmconsultar_menu`) y el
   * árbol se arma en el cliente, que es lo que antes resolvía `getMenuDiamante`
   * en el core antiguo. El árbol se persiste en IndexedDB para que el sidenav lo
   * lea, y las rutas alimentan el `AuthGuard`.
   */
  obtenerRutasPermitidas(usuario: string): Observable<string[]> {
    return this.admSeguridad.consultarMenuPorUsuario(usuario).pipe(
      map((opciones) => this.menuBuilder.construir(opciones)),
      tap(async (menuItems) => {
        try {
          await this.indexedDbService.setAuthData('menuItems', menuItems);
        } catch (error) {
          console.warn('Error guardando menuItems en IndexedDB:', error);
        }
      }),
      map((menuItems) => this.menuBuilder.extraerRutas(menuItems)),
    );
  }

  generarToken(username: string, rol: string): Observable<{ token: Token }> {
    const data = { username, rol };
    console.log('Generando token con datos:', data);
    return this.http
      .postPublic<{ token: Token }, typeof data>('token/generar', data, environment.KONG_SERVICE_URL)
      .pipe(
        catchError((error) => {
          console.error('Error al generar token', error);
          return throwError(() => new Error('No se pudo generar el token'));
        }),
      );
  }

  async setIdAuditoria(id: string): Promise<void> {
    try {
      const usuario = await this.indexedDbService.getAuthData<User>('user'); // clave 'user'
      if (usuario) {
        const usuarioActualizado = { ...usuario, idAuditoria: id };
        await this.indexedDbService.setAuthData('user', usuarioActualizado);
        await this.authState.updateUser({ idAuditoria: id });
      }
    } catch (e) {
      console.warn('No se pudo persistir idAuditoria en IndexedDB:', e);
      localStorage.setItem('auditoria', id);
    }
  }

  async guardarSesion(usuario: User, token: Token, rutas: string[]): Promise<void> {
    const refreshExpSec = Math.max(0, Math.floor((Date.parse(token.expiracionRefresco) - Date.now()) / 1000));

    const secure = location.protocol === 'https:' ? '; Secure' : '';
    document.cookie =
      `SW_RT=${encodeURIComponent(token.tokenRefresco)}; ` + `Max-Age=${refreshExpSec}; Path=/; SameSite=Lax${secure}`;

    const { tokenRefresco, ...tokenSinRT } = token as any;

    await this.authState.setSesion(usuario, tokenSinRT as Token);
    await this.authState.setRutasPermitidas(rutas);
  }

  /**
   * Persiste las rutas permitidas por separado.
   *
   * El menú vive detrás del gateway y su petición ya requiere un token válido, así
   * que la sesión debe establecerse antes de consultarlo. Por eso el guardado se
   * parte en dos: primero `guardarSesion` con las rutas vacías y, cuando el menú
   * responde, este método completa las rutas que consume el `AuthGuard`.
   */
  async guardarRutasPermitidas(rutas: string[]): Promise<void> {
    await this.authState.setRutasPermitidas(rutas);
  }

  private getCookie(name: string): string | null {
    const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : null;
  }

  async cerrarSesion(): Promise<void> {
    document.cookie = 'SW_RT=; Max-Age=0; Path=/; SameSite=Lax';

    await this.authState.cerrarSesion();
    await this.router.navigateByUrl('/login').catch((err) => {
      console.error('Error navegando:', err);
    });
  }
}
